import React, { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { CampaignInfoState } from '@/states/campaignInfoState';

import useCallAPIGetCampaignInfo from '@/hooks/api/useCallAPIGetCampaignInfo';

import { Cashback } from '@/types/api/mypage/cashbackInfo';
import { Campaign } from '@/types/api/mypage/campaignInfo';

import { Constants } from '@/utils/constants';
import Link from 'next/link';
import { formatDate } from '@/utils/commonFunc';
import Heading from '../atoms/Heading';
import LinkButton from '../atoms/LinkButton';

type Props = {
  anchor: string;
};

/**
 * キャッシュバックとキャンペーン情報を同じ表にまとめ、詳細ページへの入口も提供するセクション。
 * 特典種別ごとに表示項目が異なるため、この画面で一覧形式へ正規化して見せている。
 */
const AcceptOffer = ({ anchor }: Props) => {
  const campaignInfo = useRecoilValue(CampaignInfoState);

  const { execute: callAPIGetCampaignInfo } = useCallAPIGetCampaignInfo();

  useEffect((): void => {
    callAPIGetCampaignInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detailButton = (): React.JSX.Element => {
    return (
      <div className='m-button col1'>
        <LinkButton
          variant='secondary'
          href='/campaign/detail'
          ariaLabel='特典・キャンペーンの詳細を確認する'
        >
          詳細を確認する
        </LinkButton>
      </div>
    );
  };

  // TODO: 要リファクタリング
  /** 値を取得する: getStatusName */
  const getStatusName = (status: string): string => {
    // API の状態コードを画面表示用名称へ変換し、一覧表では人が読める表現にそろえる。
    const STATUS_CODE = Constants.Cashback.StatusCode;
    const STATUS_NAME = Constants.Cashback.StatusName;

    switch (status) {
      case STATUS_CODE.BEFORE_RECEIPT_PERIOD:
        return STATUS_NAME.BEFORE_RECEIPT_PERIOD;
      case STATUS_CODE.COMPLETE:
        return STATUS_NAME.COMPLETE;
      case STATUS_CODE.EXPIRES:
        return STATUS_NAME.EXPIRES;
      case STATUS_CODE.EXTINGUISHMENT_OF_RIGHTS:
        return STATUS_NAME.EXTINGUISHMENT_OF_RIGHTS;
      case STATUS_CODE.ERROR:
        return STATUS_NAME.ERROR;
      case STATUS_CODE.DURING_PROCEDURE:
        return STATUS_NAME.DURING_PROCEDURE;
      case STATUS_CODE.BEFORE_PROCEDURE:
        return STATUS_NAME.BEFORE_PROCEDURE;
      case STATUS_CODE.ACCOUNT_HOLDER_ILLEGAL:
        return STATUS_NAME.ACCOUNT_HOLDER_ILLEGAL;
      default:
        return '';
    }
  };

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='present'>
          特典・キャンペーン情報
        </Heading>
        <div className='m-table campaign-table'>
          <table>
            <colgroup>
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className='m-table__th'>特典・キャンペーン名称</th>
                <th className='m-table__th'>状態</th>
                <th className='m-table__th'>受取可能開始日</th>
                <th className='m-table__th'>受取可能終了日</th>
              </tr>
            </thead>
            <tbody>
              {campaignInfo.cashback_list.map((item: Cashback) => {
                return (
                  <tr key={item.code}>
                    <th className='m-table__th'>
                      <Link href='campaign/detail' legacyBehavior>
                        {item.name}
                      </Link>
                    </th>
                    <td className='m-table__td'>{getStatusName(item.status_code) || '-'}</td>
                    <td className='m-table__td'>
                      <span className='campaign-date-nowrap'>
                        {formatDate(item.period.from) || '-'}
                      </span>
                      {item.period.from ? '13:00' : ''}
                    </td>
                    <td className='m-table__td'>
                      <span className='campaign-date-nowrap'>
                        {formatDate(item.period.to) || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {campaignInfo.campaigns.map((item: Campaign) => {
                return (
                  <tr key={item.name}>
                    <th className='m-table__th'>
                      <Link href='campaign/detail' legacyBehavior>
                        {item.name}
                      </Link>
                    </th>
                    <td className='m-table__td'>-</td>
                    <td className='m-table__td'>-</td>
                    <td className='m-table__td'>-</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {detailButton()}
        </div>
      </div>
    </div>
  );
};

export default AcceptOffer;
